import axios, { AxiosError, AxiosResponse } from "axios";
import router from "../../router";
import { getWhitelistedDomains } from "../constants/whilelistedDomain";
import { getI18n } from "../../i18n";
import { showNotification } from "../../service/notification";
import { isExpiredToken } from "../../service/authorization";
import { useUserStore } from "../../store/modules/user";
import { domainsImplementingErrorCodes } from "../constants/errorCodeDomains";

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

function getDomainFromAxiosResponse(response: AxiosResponse) {
  const config = response.config;

  if (!config.url) {
    return null;
  }

  if (config.url.startsWith("http")) {
    return new URL(config.url).origin;
  }

  if (config.baseURL) {
    return new URL(config.baseURL).origin;
  }

  return null; // Cannot determine domain
}

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axios.interceptors.request.use((config) => {
  const store = useUserStore();
  const token = store.getToken;
  let isLoginRequest = false;
  let whitelistedRequest = false;

  if (config.url) {
    const { hostname } = new URL(config.url);
    whitelistedRequest = getWhitelistedDomains().includes(hostname);
    isLoginRequest = config.url.includes("authenticate");
  }

  // Only check token expiration for non-OAuth tokens
  // OAuth tokens are handled by the refresh mechanism
  if (!store.isOAuthToken && isExpiredToken(store.getToken)) {
    store.authLogout().then(() => {
      router.push({ name: "Large Language Model", force: true });
    });
    return Promise.reject("Sitzung abgelaufen");
  }

  if (token && !isLoginRequest && whitelistedRequest) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error: AxiosError) {
    const store = useUserStore();
    const originalRequest = error.config as any;
    const t = getI18n().global.t;

    if (error.response?.status === 401) {
      // Handle OAuth token refresh
      if (store.isOAuthToken && store.refreshToken && originalRequest && !originalRequest._retry) {
        if (isRefreshing) {
          // Queue the request while refresh is in progress
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = "Bearer " + token;
              return axios(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          await store.refreshOAuthToken();

          // Process queued requests with new token
          processQueue(null, store.token);

          // Retry original request with new token
          originalRequest.headers["Authorization"] = "Bearer " + store.token;
          return axios(originalRequest);
        } catch (refreshError) {
          // Refresh failed, process queue with error
          processQueue(refreshError, null);

          // Set auth expired and redirect
          if (!store.authExpired) {
            store.setAuthExpired();
            router.push({ name: "Large Language Model", force: true });
          }

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Non-OAuth token or no refresh token available
        if (!store.authExpired) {
          store.setAuthExpired();
          router.push({ name: "Large Language Model", force: true });
        }
      }
    } else if (error.response?.status === 403) {
      showNotification("error", (t as any)("auth.error"), "Ihrem User Account fehlt die Berechtigung, diese Aktion auszuführen.");
    } else if (error.response && getDomainFromAxiosResponse(error.response) && domainsImplementingErrorCodes.includes(getDomainFromAxiosResponse(error.response)!) && error.response?.status >= 400) {
      const errorData = error.response?.data as { code: string | number; message?: unknown; detail?: unknown };
      const code = "errors.backend." + (errorData?.code || "unknown_error");
      // Prefer the backend's human-readable message; fall back to the translated error code.
      const messageText =
        typeof errorData?.message === "string" && errorData.message ? errorData.message : (t as any)(code);
      const customError = {
        message: messageText,
        code,
        // Upstream (e.g. Cases) diagnostic detail forwarded by the orchestrator.
        // Not displayed to the user; surfaced only in the debug trace.
        detail: errorData?.detail,
      };
      // notifyError(customError);
      return Promise.reject(customError);
    }

    return Promise.reject(error);
  }
);

export default axios;
